/**
 * EHR-door analytics emitters, as plain functions.
 *
 * These were `analyticsScribe.trackNoteSynced`, `.trackNoteSyncFailed`,
 * `.trackContextLinked`, `.trackContextUnlinked`, `.trackIntegrationUpdated` and
 * `.trackIntegrationUserSearched` — model methods whose entire body was a
 * `trackEventV2` call, reachable only through `dispatch`. The `integrations/ehr`
 * hooks call these instead, which is what takes the Rematch leg off them; the
 * emitted payloads are unchanged.
 *
 * Every emitter forwards through `trackV2`, which is now a pure alias of
 * `@/lib/analytics/track` — the two are one binding, and destination policy is
 * applied by event name inside the pipeline, so neither name can route
 * differently from the other. The choice used to be load-bearing (`trackV2`
 * owned the Snowplow denylist and `track()` skipped it); it is now inertia, and
 * these calls are left as they are rather than churned.
 *
 * THE v1 COMPOUND IS DELIBERATELY NOT HERE. `trackConnectIntegration` fires a v1
 * Segment event as well as a V2 one, and the v1 sender
 * (`@/lib/analytics/track-legacy-event`) reaches `analyticsScribeModel`, the
 * integration atoms and `posthog-js` — the whole Rematch model cluster. Putting
 * it in this module would drag that graph into every hook that imports one line
 * from here, which is the opposite of what severing the bus is for, and it was
 * measured rather than assumed: five suites over these call sites fail on
 * partial `@tanstack/react-query` / `jotai` mocks the moment that specifier is
 * in the graph. It lives in `./ehr-connect-events` instead.
 */
import { trackV2 } from '@/lib/analytics/track-v2';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import type {
  IntegrationNoteSyncedEventProps,
  IntegrationUpdatedEventProps,
  IntegrationUserSearchedEventProps,
} from '@/models/analytics/features/integrations';
import type {
  ContextLinkedEventProps,
  ContextUnlinkedEventProps,
} from '@/models/analytics/features/past-context';

/** Fired when a note is synced/pushed to an EHR. */
export function trackNoteSynced(input: IntegrationNoteSyncedEventProps): void {
  trackV2({
    options: { amplitude: false },
    event_name: TrackingEventName.NoteSynced,
    props: input,
  });
}

/** Fired when a note failed to sync/push to an EHR. */
export function trackNoteSyncFailed(input: IntegrationNoteSyncedEventProps): void {
  trackV2({
    options: { amplitude: false },
    event_name: TrackingEventName.NoteSyncFailed,
    props: input,
  });
}

/** Fired when a past EHR note or session is linked as context. */
export function trackContextLinked(input: ContextLinkedEventProps): void {
  trackV2({
    options: { amplitude: false },
    event_name: TrackingEventName.ContextLinked,
    props: input,
  });
}

/** Fired when a past EHR note or session is unlinked as context. */
export function trackContextUnlinked(input: ContextUnlinkedEventProps): void {
  trackV2({
    options: { amplitude: false },
    event_name: TrackingEventName.ContextUnlinked,
    props: input,
  });
}

/** Fired when an integration configuration is updated. */
export function trackIntegrationUpdated(input: IntegrationUpdatedEventProps): void {
  trackV2({
    options: { amplitude: false },
    event_name: TrackingEventName.IntegrationUpdated,
    props: input,
  });
}

/** Fired when an EHR user is searched for during integration setup. */
export function trackIntegrationUserSearched(
  input: IntegrationUserSearchedEventProps
): void {
  trackV2({
    options: { amplitude: false },
    event_name: TrackingEventName.IntegrationUserSearched,
    props: input,
  });
}
